import { CommImageNameComp } from "custom_modules/Members/CommonComponents";
import moment from "moment-timezone";
import { useEffect, useState } from "react";
import { Modal, ModalBody } from "reactstrap";
import styled from "styled-components";

import avatar5 from "../../assets/img/avatar_5.svg";
import crossIcon from "../../assets/img/icons/cross_black.svg";
import deleteIcon from "../../assets/img/icons/delete_white.svg";
import editIcon from "../../assets/img/icons/edit-white-shift.svg";
import AddMultipleAssigneeDropdown from "../../components/CustomDropdown/AddMultipleAssigneeDropdown";
import ButtonTextLoader from "../../components/Loaders/ButtonTextLoader";
import RoundImageNameComp from "../../components/RoundImageName/RoundImageNameComp";
import {
  ColoredButton,
  PrimaryButton,
  SecondaryButton,
  WhiteButton,
} from "../../styledComponents/buttons";
import {
  CardTitle,
  CommonGrid,
  CommonText,
  InputLabel,
  StyledLink,
} from "../../styledComponents/common";
import { AssigneeSection } from "../../styledComponents/createProject";
import { getRandomColor } from "../../utils/getRandomColor";
import { formatDurationToHoursMinutes, getFullName } from "../../utils/helper";

const SlotContainer = styled.div`
  display: grid;
  gap: 20px;
  margin-top: 20px;
`;

const ColoredCircle = styled.div`
  position: absolute;
  top: -12px;
  right: ${props => (props.edit ? `52px` : `20px`)};
  display: none;
  z-index: 5;
  width: 25px;
  height: 25px;
  border-radius: 50%;
  background-color: ${props => (props.edit ? `#7F9DBF` : `#FE5969`)};
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const TimeDiv = styled.div`
  position: relative;
  border-radius: ${({ theme }) => theme.borderRadii.button};
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: ${({ theme }) => theme.font.inputLabel.size};
  color: ${({ theme }) => theme.font.pageText.colorSecondary};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  background: ${({ bgColor }) => bgColor};
  padding: 15px;
  &:hover ${ColoredCircle} {
    display: grid;
  }
`;
const TextSection = styled.div`
  display: grid;
  gap: 5px;
`;
const ClosePopup = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 1;
  cursor: pointer;
`;
const ButtonContainer = styled.div`
  display: flex;
  gap: 15px;
  justify-content: space-between;
  margin-top: 30px;
`;

// Helper function to parse datetime string dynamically
// Handles both timezone-aware (with Z, +, -) and timezone-naive (assumed UTC) strings
const parseDateTimeInTimezone = (datetimeString, targetTimezone) => {
  if (!datetimeString) return null;
  try {
    // Check if datetime string has timezone info (Z, +, or - at position 10 or later)
    if (
      datetimeString.includes("Z") ||
      datetimeString.includes("+") ||
      datetimeString.includes("-", 10)
    ) {
      // Has timezone info, parse directly and convert to target timezone
      return moment(datetimeString).tz(targetTimezone);
    } else {
      // No timezone info, assume UTC and convert to target timezone
      return moment.utc(datetimeString).tz(targetTimezone);
    }
  } catch (error) {
    console.error("Error parsing datetime:", error);
    return null;
  }
};

const ShiftDetailsPopup = ({
  selectedOrganization,
  isOpen,
  toggle,
  shift,
  deleteToggle,
  editToggle,
  editJobToggle,
  setSelectedJob,
  history,
  orgMembersShortList,
  memberShortListIsLoading,
  getOrganizationMembersShortList,
  editShift,
  updateJobRequest,
}) => {
  const orgType = selectedOrganization?.type || "operation";
  const timeZone = selectedOrganization?.timezone || moment.tz.guess();

  // State for selected assignees
  const [selectedAssignees, setSelectedAssignees] = useState([]);

  // Initialize assignees from shift when shift changes
  useEffect(() => {
    if (shift && shift.assignees && Array.isArray(shift.assignees)) {
      // Map assignees to the format expected by AddMultipleAssigneeDropdown
      const mappedAssignees = shift.assignees.map(assignee => ({
        id: assignee.id,
        name:
          `${assignee.first_name} ${assignee.last_name}`.trim() ||
          assignee.first_name,
        email: assignee.email || "",
        avatar: assignee.avatar || "",
        color: assignee.color || "",
        phone: assignee.phone || "",
        first_name: assignee.first_name || "",
        last_name: assignee.last_name || "",
      }));
      setSelectedAssignees(mappedAssignees);
    } else {
      setSelectedAssignees([]);
    }
  }, [shift]);

  // Fetch organization members list when modal opens
  useEffect(() => {
    if (isOpen && selectedOrganization && selectedOrganization.id) {
      getOrganizationMembersShortList({
        organization_id: selectedOrganization.id,
      });
    }
  }, [isOpen, selectedOrganization, getOrganizationMembersShortList]);

  // Handler for assigning a member
  const handleAssignMember = user => {
    if (user && user.id) {
      // Update local state immediately
      const newAssignees = [...selectedAssignees, user];
      setSelectedAssignees(newAssignees);

      // Call API to update assignees
      updateAssignees(newAssignees);
    }
  };

  // Handler for removing a member
  const handleRemoveMember = user => {
    if (user && user.id) {
      // Update local state immediately
      const newAssignees = selectedAssignees.filter(
        member => member.id !== user.id,
      );
      setSelectedAssignees(newAssignees);

      // Call API to update assignees
      updateAssignees(newAssignees);
    }
  };

  // Function to update assignees via API
  const updateAssignees = assignees => {
    if (
      !shift ||
      !shift.id ||
      !selectedOrganization ||
      !selectedOrganization.id
    ) {
      return;
    }

    const assigneeIds = assignees.map(assignee => assignee.id);
    const isJobShift = shift.work_order && shift.work_order.id;

    if (isJobShift) {
      // For job shifts, use updateJobRequest
      const payload = {
        organization_id: selectedOrganization.id,
        workOrder_id: shift.work_order.id,
        job_id: shift.id,
        assignees: assigneeIds,
      };
      updateJobRequest(payload);
    } else {
      // For office shifts, use editShift
      const payload = {
        organization_id: selectedOrganization.id,
        shift_id: shift.id,
        assignees: assigneeIds,
      };
      editShift(payload);
    }
  };

  const handleShiftDelete = () => {
    toggle();
    setTimeout(deleteToggle, 300);
  };

  const handleShiftEdit = () => {
    // If it's a job schedule (has work_order.id), open EditJobPopup
    // Otherwise, open EditShift modal
    if (shift && shift.work_order && shift.work_order.id) {
      // Set the selected job first, then close this modal, then open edit job modal
      setSelectedJob(shift);
      toggle();
      setTimeout(() => {
        editJobToggle();
      }, 300);
    } else {
      // For office schedules, just close this modal and open edit shift modal
      toggle();
      setTimeout(() => {
        editToggle();
      }, 300);
    }
  };
  // Determine whether to show add-assignee control (hide if shift start time has passed)
  const showAddAssignee = (() => {
    if (!shift || !shift.start_time) return true;
    const startMoment = parseDateTimeInTimezone(shift.start_time, timeZone);
    if (!startMoment) return true;
    const now = moment.tz(timeZone);
    return startMoment.isAfter(now);
  })();
  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      style={{ marginTop: `15vh`, maxWidth: `355px` }}
    >
      <ModalBody style={{ padding: `20px` }}>
        <CardTitle margin="0 0 20px 10px">Shift Details</CardTitle>
        <ClosePopup onClick={() => toggle()}>
          <img src={crossIcon} alt="" width="30px" />
        </ClosePopup>
        {shift && Object.keys(shift).length > 0 && (
          <SlotContainer>
            <TimeDiv
              bgColor={
                shift && shift.theme_color ? shift.theme_color : `#D2E4FE`
              }
            >
              <TextSection>
                <CardTitle fontSize="14px" fontWeight="600">
                  {shift.name}
                </CardTitle>
                <CommonText name fontSize="13px">
                  <span style={{ fontWeight: `600` }}>Type: </span>
                  {shift.work_order && shift.work_order.id
                    ? orgType === "operation" ? "Task Schedule" : "Job Schedule"
                    : "Team Schedule"}
                </CommonText>
                <CommonText name fontSize="13px" margin="15px 0 auto 0">
                  <span style={{ fontWeight: `600` }}>Slot: </span>{" "}
                  {shift.start_time &&
                    parseDateTimeInTimezone(shift.start_time, timeZone)?.format(
                      "h:mm a",
                    )}{" "}
                  -{" "}
                  {shift.end_time &&
                    parseDateTimeInTimezone(shift.end_time, timeZone)?.format(
                      "h:mm a",
                    )}{" "}
                  <span style={{ fontWeight: `600`, marginLeft: `20px` }}>
                    Duration:{" "}
                  </span>
                  <span>
                    {shift.start_time &&
                      shift.end_time &&
                      (() => {
                        const startMoment = parseDateTimeInTimezone(
                          shift.start_time,
                          timeZone,
                        );
                        const endMoment = parseDateTimeInTimezone(
                          shift.end_time,
                          timeZone,
                        );
                        if (startMoment && endMoment) {
                          return formatDurationToHoursMinutes(
                            moment
                              .duration(endMoment.diff(startMoment))
                              .asSeconds(),
                          );
                        }
                        return null;
                      })()}
                  </span>
                </CommonText>
                <CommonText name fontSize="13px">
                  {" "}
                  {shift.start_time &&
                    parseDateTimeInTimezone(shift.start_time, timeZone)?.format(
                      "dddd, D MMM YYYY",
                    )}
                </CommonText>
                {shift.work_order && shift.work_order.id && (
                  <>
                    {orgType === "operation" && (
                      <CommonText name fontSize="13px" margin="15px 0 auto 0">
                        <span style={{ fontWeight: `600` }}>
                          {"Project"}
                          :{" "}
                        </span>
                        {shift.work_order.name}
                      </CommonText>
                    )}
                    {orgType === "service" && (
                      <CommonText name fontSize="13px">
                        <span style={{ fontWeight: `600` }}>Customer: </span>
                        {shift.work_order.client_name &&
                          shift.work_order.client_name}
                      </CommonText>
                    )}
                    {shift.location &&
                      shift.location.id &&
                      shift.location.name && (
                        <>
                          <CommonText
                            name
                            fontSize="13px"
                            margin="15px 0 auto 0"
                          >
                            <span style={{ fontWeight: `600` }}>
                              Location:{" "}
                            </span>
                            {shift.location.name}
                          </CommonText>
                          <CommonText name fontSize="13px">
                            <span style={{ fontWeight: `600` }}>Address: </span>
                            {shift.location.address}
                          </CommonText>
                        </>
                      )}
                  </>
                )}
              </TextSection>
            </TimeDiv>
            <AssigneeSection>
              <InputLabel>Assigned Member</InputLabel>

              <AddMultipleAssigneeDropdown
                numberOfVisibleMembers={5}
                isLoading={memberShortListIsLoading}
                assigneesList={selectedAssignees}
                membersList={orgMembersShortList}
                handleAssign={handleAssignMember}
                handleRemove={handleRemoveMember}
                showAddAssignee={showAddAssignee}
              />
            </AssigneeSection>
            <ButtonContainer>
              {shift.work_order && shift.work_order.id && (
                <PrimaryButton
                  padding="0 20px"
                  borderRadius="30px"
                  onClick={() =>
                    history.push(
                      `/user/${
                        (selectedOrganization?.type || "operation") ===
                        "service"
                          ? "job-details"
                          : "task-details"
                      }?id=${shift.id}&${
                        (selectedOrganization?.type || "operation") ===
                        "service"
                          ? "job_id"
                          : "task_id"
                      }=${shift.work_order.id}`,
                    )
                  }
                >
                  {(selectedOrganization?.type || "operation") === "service"
                    ? "Job Details"
                    : "Task Details"}
                </PrimaryButton>
              )}
              {selectedOrganization &&
              selectedOrganization.role &&
              (selectedOrganization.role === "admin" ||
                selectedOrganization.role === "owner") &&
              (!shift.work_order ||
                (shift.status &&
                  (shift.status === "unscheduled" ||
                    shift.status === "scheduled" ||
                    shift.status === "ready to dispatch" ||
                    shift.status === "totally missed"))) ? (
                <ColoredButton
                  type="delete"
                  padding="0 20px"
                  onClick={() => handleShiftDelete()}
                >
                  Delete
                </ColoredButton>
              ) : null}
              {selectedOrganization &&
              selectedOrganization.role &&
              (selectedOrganization.role === "admin" ||
                selectedOrganization.role === "owner") &&
              (!shift.work_order ||
                (shift.work_order &&
                  shift.work_order.id &&
                  shift.status &&
                  (shift.status === "unscheduled" ||
                    shift.status === "scheduled" ||
                    shift.status === "ready to dispatch"))) &&
              // Hide edit button if shift start time has passed
              (() => {
                if (!shift.start_time) return true;
                const startMoment = parseDateTimeInTimezone(
                  shift.start_time,
                  timeZone,
                );
                if (!startMoment) return true;
                const now = moment.tz(timeZone);
                // Exception: if is_anytime is true and date is today, allow editing
                if (shift.is_anytime && startMoment.isSame(now, 'day')) {
                  return true;
                }
                return startMoment.isAfter(now);
              })() ? (
                <WhiteButton
                  padding="0 20px"
                  onClick={() => handleShiftEdit(shift)}
                >
                  Edit
                </WhiteButton>
              ) : null}
            </ButtonContainer>
          </SlotContainer>
        )}
      </ModalBody>
    </Modal>
  );
};

export default ShiftDetailsPopup;
